import React, { useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  IMPACT_OWNER_FIRSTNAME,
  IMPACT_OWNER_LASTNAME,
  ImpactReportCategory,
  ImpactReportUseCase,
  ImpactType,
  Stages,
} from '@/impactReports/impactReport.types';
import { UserNameCellProps, UserNameCellRenderer } from '@/impactReports/cellRenderers/UserNameCellRenderer.atom';
import { getImpactStageIcon, replaceLinks } from '@/impactReports/impactReport.utilities';
import { Button, Icon } from '@seeqdev/qomponents';
import { ICellRendererParams } from 'ag-grid-community';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { SelectedWorksheetDisplayAndLink } from '@/impactReports/SelectedWorksheetDisplayAndLink.atom';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { sqImpactReportStore } from '@/core/core.stores';
import { deleteTableRows } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { errorToast } from '@/utilities/toast.utilities';
import { setForceTableReloadFlag } from '@/impactReports/impactReport.actions';
import {
  getCategoryDisplayString,
  getCustomCategoryTitle,
  getImpactTypeDisplayString,
  getKPIDisplayName,
  getKPIUnit,
  getStageDisplayString,
} from '@/impactReports/impactReportDisplay.utilities';
import { decorateMaterializedTableWithImpactValue, getValueFormatter } from '@/impactReports/tiles/table.utilities';
import { useAsyncEffect } from 'rooks';

interface ImpactReportDetailsProps extends ImpactReportUseCase {
  categories: ImpactReportCategory[] | undefined;
  [key: string]: any;
  onClose: () => void;
  onEdit?: () => void;
  editable?: boolean;
}

interface ColumnItem {
  title: string;
  value: string | undefined;
}

export const ImpactReportDetailHeader: React.FunctionComponent<ImpactReportUseCase> = (props) => {
  const params = {
    value: {
      first: props[IMPACT_OWNER_FIRSTNAME]!,
      last: props[IMPACT_OWNER_LASTNAME]!,
    },
  } as ICellRendererParams<any, UserNameCellProps>;
  const location = props[SeeqNames.ImpactReport.Location] || null;
  const locationObj = location !== null ? JSON.parse(location) : null;
  const locationDisplay = locationObj ? locationObj.label : undefined;
  const site = props[SeeqNames.ImpactReport.ImpactSite] || undefined;
  const locationAndSite = _.compact([locationDisplay, site]).join(', ');
  return (
    <div className="flexRowContainer valueCapture width-maximum">
      <div className="displayFlex flexSpaceBetween flexAlignCenter mb8 flexFill">
        <div className="flexRowContainer">
          <UserNameCellRenderer {...params} />
        </div>

        <div className="flexColumnContainer flexAlignEnd flexAlignCenter borderGray fakeBadge">
          <Icon
            type="theme"
            testId="stageIcon"
            icon={getImpactStageIcon(props[SeeqNames.ImpactReport.ImpactStatus] as Stages)}
            extraClassNames="mr5 mb3"
          />
          {getStageDisplayString(props[SeeqNames.ImpactReport.ImpactStatus] as Stages)}
        </div>
      </div>
      <h2 className="mb10">{props[SeeqNames.ImpactReport.ImpactName]}</h2>
      {locationAndSite}
      <br />
      <SelectedWorksheetDisplayAndLink
        worksheetId={props[SeeqNames.ImpactReport.AssignedToWorksheet]!}
        workbookId={props[SeeqNames.ImpactReport.AssignedToWorkbook]!}
      />
    </div>
  );
};
export const ImpactReportDetail: React.FunctionComponent<ImpactReportDetailsProps> = (props) => {
  const { t } = useTranslation();
  const { categories, onClose, onEdit, id } = props;
  const tableId = useFluxPath(sqImpactReportStore, () => sqImpactReportStore.tableId);
  const kpiColumns = useFluxPath(sqImpactReportStore, () => sqImpactReportStore.kpiColumns);
  const [kpiDisplays, setKpiDisplays] = useState<ColumnItem[]>([]);
  const deleteUseCase = async () => {
    try {
      await deleteTableRows(tableId!, [
        {
          itemId: props[SeeqNames.MaterializedTables.ItemIdColumn],
          datumId: props[SeeqNames.MaterializedTables.DatumIdColumn],
        },
      ]);
    } catch (e) {
      errorToast({ messageKey: 'IMPACT_REPORT.NO_DELETE' });
    }
    setForceTableReloadFlag(true);
    onClose();
  };

  useAsyncEffect(async () => {
    const data = await decorateMaterializedTableWithImpactValue(
      [props],
      kpiColumns ?? [],
      moment().subtract(1, 'year').toISOString(),
      moment().toISOString(),
    );
    const kpiWithValue = kpiColumns.map((item) => {
      const unit = getKPIUnit(item.columnName);
      const formatterFn = getValueFormatter(unit);
      const value = data[0][item.columnName];
      return { title: getKPIDisplayName(item.columnName), value: !value ? '-' : formatterFn(value, unit) };
    });
    setKpiDisplays(kpiWithValue ?? []);
  }, [props]);

  const categoryDisplays =
    categories?.map((item) => {
      const display =
        (item.category?.id && getCategoryDisplayString(props[item.category.id], item.options)) ?? undefined;
      return { title: getCustomCategoryTitle(item), value: display };
    }) ?? [];

  const columns = [
    {
      title: t('IMPACT_REPORT.TABLE_HEADERS.TYPE'),
      value: getImpactTypeDisplayString(props[SeeqNames.ImpactReport.ImpactSavingsType] as ImpactType),
    },
    ...categoryDisplays,
  ];

  return (
    <div className="flexCenter p25 height-maximum overflowYAuto valueCapture">
      <div className="detailsView flexRowContainer flexFill">
        <div className="flexRowContainer mb10">
          <ContainerWithHTML
            content={replaceLinks(
              props[SeeqNames.ImpactReport.ImpactDescription] ?? '',
              props[SeeqNames.ImpactReport.AssignedToWorkbook],
              props[SeeqNames.ImpactReport.AssignedToWorksheet],
            )}
            isBlock
          />
        </div>

        <div className="container ml10">
          <h4>{t('IMPACT_REPORT.TOTAL_IMPACT_PAST_YEAR')}</h4>
          {kpiDisplays
            ?.reduce<ColumnItem[][]>((rows, item, index) => {
              if (index % 2 === 0) {
                rows.push(kpiDisplays?.slice(index, index + 2));
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <div className="row" key={rowIndex}>
                {row.map((item, colIndex) => {
                  return (
                    <div className="col" key={colIndex}>
                      <div className="valueCaptureStats">{item.title}</div>
                      <p>{item.value}</p>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
        <hr className="mb6" />
        <div className="container ml10">
          {columns
            ?.reduce<ColumnItem[][]>((rows, item, index) => {
              if (index % 2 === 0) {
                rows.push(columns?.slice(index, index + 2));
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <div className="row" key={rowIndex}>
                {row.map((item, colIndex) => {
                  return (
                    <div className="col" key={colIndex}>
                      <div className="valueCaptureStats">{item.title}</div>
                      <p>{item.value}</p>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>

      <div className="flexColumnContainer mt25 flexFill">
        <div className="flexColumnContainer flexFill">
          {onEdit && <Button label={t('DELETE')} onClick={deleteUseCase} variant="danger" testId="deleteImpact" />}
        </div>
        <Button testId="closeImpactDetail" label={t('CLOSE')} onClick={onClose} extraClassNames="mr10" />
        {onEdit && <Button label={t('EDIT')} onClick={onEdit} variant="theme" testId="editImpact" />}
      </div>
    </div>
  );
};
